import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Heart, Baby, BookOpen, Shield, Users, Calendar, Star , Leaf} from 'lucide-react';

const Ressources = () => {
  const medicalSites = [
    {
      name: "HAS - Haute Autorité de Santé",
      description: "Recommandations officielles sur la grossesse et l'accouchement",
      url: "https://www.has-sante.fr/jcms/c_2820336/fr/accouchement-normal-accompagnement-de-la-physiologie-et-interventions-medicales",
      icon: Shield,
      category: "Autorité sanitaire"
    },
    {
      name: "Ameli - Assurance Maladie",
      description: "Informations sur le suivi de grossesse et les démarches",
      url: "https://www.ameli.fr/assure/droits-demarches/famille/maternite-paternite-adoption/grossesse",
      icon: Heart,
      category: "Sécurité sociale"
    },
    {
      name: "Ordre des Sages-femmes",
      description: "Informations sur la profession et les bonnes pratiques",
      url: "https://www.ordre-sages-femmes.fr/",
      icon: Users,
      category: "Profession"
    }
  ];

  const parentResources = [
    {
      name: "Guide grossesse - Ameli",
      description: "Repères officiels pour le suivi de grossesse",
      url: "https://www.ameli.fr/assure/sante/themes/grossesse",
      icon: BookOpen,
      category: "Grossesse"
    },
    {
      name: "Dépistage de la trisomie 21 (HAS)",
      description: "Informations sur le dépistage prénatal de la trisomie",
      url: "https://www.has-sante.fr/jcms/c_2841773/fr/depistage-combine-du-1er-trimestre",
      icon: Shield,
      category: "Dépistage"
    },
    {
      name: "Infos groupe sanguin et Rhésus (Ameli)",
      description: "Incompatibilité fœto-maternelle et prévention",
      url: "https://www.ameli.fr/assure/sante/themes/grossesse/suivi-de-grossesse",
      icon: Heart,
      category: "Biologie"
    },
    {
      name: "Dépistage du diabète gestationnel (HAS)",
      description: "Recommandations et conduite à tenir",
      url: "https://www.has-sante.fr/jcms/c_2867126",
      icon: Shield,
      category: "Dépistage"
    },
    {
      name: "1000 premiers jours",
      description: "Pourquoi les 1000 premiers jours sont déterminants",
      url: "https://www.1000-premiers-jours.fr/",
      icon: Baby,
      category: "Parentalité"
    },
    {
      name: "La Leche League France",
      description: "Soutien et informations sur l'allaitement maternel",
      url: "https://www.lllfrance.org/",
      icon: Heart,
      category: "Allaitement"
    },
    {
      name: "Projet Fées",
      description: "Notre environnement, notre bébé, sa santé",
      url: "https://www.projetfees.fr/wp-content/uploads/2024/10/livret-FALC-FEES-ML.pdf",
      icon: Leaf,
      category: "Environnement"
    }
  ];

  const emergencyContacts = [
    {
      name: "SAMU",
      description: "Urgences médicales",
      url: "tel:15",
      icon: Shield,
      category: "Urgence"
    },
    {
      name: "Pompiers",
      description: "Urgences générales",
      url: "tel:18",
      icon: Shield,
      category: "Urgence"
    },
    {
      name: "Stop violences femme",
      description: "Numéro d'urgence",
      url: "tel:3919",
      icon: Shield,
      category: "Urgence"
    }
  ];

  return (
    <section id="ressources" className="py-20 bg-gradient-to-b from-warm-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-sage-800 mb-6">
            Ressources utiles
          </h2>
          <p className="text-xl text-sage-600 max-w-3xl mx-auto leading-relaxed">
            Découvrez une sélection de sites fiables et de ressources pour vous accompagner 
            dans votre parcours de parentalité et vous informer sur la santé.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Sites médicaux */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="lg:col-span-1"
          >
            <div className="bg-white rounded-2xl shadow-lg p-8 h-full">
              <div className="text-center mb-6">
                <div className="flex justify-center mb-4">
                  <div className="bg-primary-100 p-3 rounded-xl">
                    <Shield className="w-6 h-6 text-primary-600" />
                  </div>
                </div>
                <h3 className="font-display text-2xl font-semibold text-sage-800">
                  Sites médicaux officiels
                </h3>
              </div>
              <p className="text-sage-600 mb-6">
                Sources fiables d'informations médicales et de recommandations officielles.
              </p>
              <div className="space-y-4">
                {medicalSites.map((site, index) => {
                  const Icon = site.icon;
                  return (
                    <motion.a
                      key={site.name}
                      href={site.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Visiter ${site.name} - ${site.description} (s'ouvre dans un nouvel onglet)`}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      whileHover={{ y: -2 }}
                      viewport={{ once: true }}
                      className="block p-4 rounded-xl border border-sage-200 hover:border-primary-300 hover:shadow-md transition-all duration-300 group"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="text-center mb-2">
                            <div className="flex justify-center items-center mb-2">
                              <Icon className="w-4 h-4 text-primary-500 mr-2" />
                              <h4 className="font-semibold text-sage-800 group-hover:text-primary-600 transition-colors">
                                {site.name}
                              </h4>
                            </div>
                          </div>
                          <p className="text-sm text-sage-600 mb-1">{site.description}</p>
                          <span className="text-xs text-primary-500 bg-primary-50 px-2 py-1 rounded-full">
                            {site.category}
                          </span>
                        </div>
                        <ExternalLink className="w-4 h-4 text-sage-400 group-hover:text-primary-500 transition-colors" />
                      </div>
                    </motion.a>
                  );
                })}
              </div>
            </div>
          </motion.div>

          {/* Ressources pour parents */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="lg:col-span-1"
          >
            <div className="bg-white rounded-2xl shadow-lg p-8 h-full">
              <div className="text-center mb-6">
                <div className="flex justify-center mb-4">
                  <div className="bg-warm-100 p-3 rounded-xl">
                    <Baby className="w-6 h-6 text-warm-600" />
                  </div>
                </div>
                <h3 className="font-display text-2xl font-semibold text-sage-800">
                  Ressources pour parents
                </h3>
              </div>
              <p className="text-sage-600 mb-6">
                Sites et associations pour vous accompagner dans votre parentalité.
              </p>
              <div className="space-y-4">
                {parentResources.map((resource, index) => {
                  const Icon = resource.icon;
                  return (
                    <motion.a
                      key={resource.name}
                      href={resource.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Visiter ${resource.name} - ${resource.description} (s'ouvre dans un nouvel onglet)`}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      whileHover={{ y: -2 }}
                      viewport={{ once: true }}
                      className="block p-4 rounded-xl border border-sage-200 hover:border-warm-300 hover:shadow-md transition-all duration-300 group"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="text-center mb-2">
                            <div className="flex justify-center items-center mb-2">
                              <Icon className="w-4 h-4 text-warm-500 mr-2" />
                              <h4 className="font-semibold text-sage-800 group-hover:text-warm-600 transition-colors">
                                {resource.name}
                              </h4>
                            </div>
                          </div>
                          <p className="text-sm text-sage-600 mb-1">{resource.description}</p>
                          <span className="text-xs text-warm-500 bg-warm-50 px-2 py-1 rounded-full">
                            {resource.category}
                          </span>
                        </div>
                        <ExternalLink className="w-4 h-4 text-sage-400 group-hover:text-warm-500 transition-colors" />
                      </div>
                    </motion.a>
                  );
                })}
              </div>
            </div>
          </motion.div>

          {/* Numéros d'urgence */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
            className="lg:col-span-1"
          >
            <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-2xl shadow-lg p-8 h-full">
              <div className="text-center mb-6">
                <div className="flex justify-center mb-4">
                  <div className="bg-primary-200 p-3 rounded-xl">
                    <Shield className="w-6 h-6 text-primary-700" />
                  </div>
                </div>
                <h3 className="font-display text-2xl font-semibold text-sage-800">
                  Numéros d'urgence
                </h3>
              </div>
              <p className="text-sage-600 mb-6">
                En cas d'urgence, composez directement ces numéros.
              </p>
              <div className="space-y-4">
                {emergencyContacts.map((contact, index) => {
                  const Icon = contact.icon;
                  return (
                    <motion.a
                      key={contact.name}
                      href={contact.url}
                      aria-label={`Appeler ${contact.name} - ${contact.description}`}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      whileHover={{ y: -2 }}
                      viewport={{ once: true }}
                      className="block p-4 rounded-xl bg-white border border-primary-200 hover:border-primary-400 hover:shadow-md transition-all duration-300 group"
                    >
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center min-w-0">
                          <Icon className="w-5 h-5 text-primary-600 mr-3" />
                          <div>
                            <h4 className="font-semibold text-sage-800 group-hover:text-primary-600 transition-colors">
                              {contact.name}
                            </h4>
                            <p className="text-sm text-sage-600">{contact.description}</p>
                          </div>
                        </div>
                        <div className="text-primary-600 font-bold text-lg break-words sm:text-right">
                          {contact.url.replace('tel:', '')}
                        </div>
                      </div>
                    </motion.a>
                  );
                })}
              </div>
              
              {/* Avertissement */}
              <div className="mt-6 p-4 bg-primary-200 rounded-xl">
                <div className="flex items-start">
                  <Star className="w-5 h-5 text-primary-700 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-primary-800 text-sm font-medium mb-1">
                      Important
                    </p>
                    <p className="text-primary-700 text-sm">
                      En cas de doute ou d'urgence, n'hésitez pas à me contacter directement 
                      ou à vous rendre aux urgences de la maternité la plus proche.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Call to action */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl p-8 lg:p-12">
            <h3 className="font-display text-3xl lg:text-4xl font-bold text-white mb-4">
              Besoin d'informations personnalisées ?
            </h3>
            <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
              Ces ressources sont un excellent point de départ, mais chaque situation est unique. 
              N'hésitez pas à me consulter pour des conseils adaptés à votre situation.
            </p>
            <motion.a
              href="#contact"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              className="inline-block bg-white text-primary-700 px-8 py-4 rounded-full font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Me consulter
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Ressources; 